import { CartAddEvent, CartUpdateEvent } from '@theme/events';

// Debug logging toggle. Flip to `true` to surface reconciler diagnostics in the
// console; keep `false` in production so it stays silent.
const DEBUG = false;

const log = (...args) => {
  if (DEBUG) console.log('[gift-reconciler]', ...args);
};
const warn = (...args) => {
  if (DEBUG) console.warn('[gift-reconciler]', ...args);
};

/**
 * Gift reconciler.
 *
 * The Case Builder rewards ladder unlocks free "gifts" — physical products
 * the customer receives as line items in their order. Gifts are tied to the
 * total SUBSCRIPTION bottle count in the cart (one-time products don't
 * count). Because the count drifts as the customer edits the cart, the gift
 * line items have to be reconciled with the current cart state on every
 * cart update.
 *
 * Runs globally (loaded once via `snippets/header-actions.liquid`).
 *
 * On every `cart:update` / `cart-add` event:
 *   1. Fetch the helper section (`/?section_id=product-buy-flow-cart-bottles`)
 *      — its JSON snapshot carries cart lines (with `subscription` flag,
 *      `key`, `giftFor` line property) plus the active gifts_config tiers
 *      (variant ids + bottle requirements).
 *   2. Count SUBSCRIPTION bottles, skipping any existing gift lines (those
 *      have `giftFor` set so they don't bootstrap themselves).
 *   3. Earned tier ids = those whose `required` ≤ count.
 *   4. Diff against currently-present gift lines:
 *        - Earned but missing → POST `/cart/add.js` with `_gift_for` stamp.
 *        - Present but not earned → POST `/cart/update.js` with key=0.
 *   5. Dispatch `CartUpdateEvent` so the cart drawer + bubble re-render.
 *
 * Loops are prevented by a `#running` guard — the reconciler also fires on
 * the event it dispatches, but the second pass sees no diff and exits.
 *
 * Plan 3 (Discount Function) will gate the actual 100%-off discount on
 * server-side eligibility (customer cooldown + address hash). This client
 * only handles the LINE existence; the price discount lives in the Function.
 */
class GiftReconciler {
  #running = false;

  constructor() {
    document.addEventListener('cart:update', this.#reconcile);
    document.addEventListener(CartAddEvent.eventName, this.#reconcile);
    // First pass on page load — covers the case where the customer landed
    // on the cart drawer with a stale set of gift lines vs current cart.
    queueMicrotask(this.#reconcile);
  }

  #reconcile = async () => {
    if (this.#running) return;
    this.#running = true;
    try {
      const res = await fetch('/?section_id=product-buy-flow-cart-bottles', { cache: 'no-store' });
      if (!res.ok) {
        warn('section fetch not ok', res.status);
        return;
      }
      const html = await res.text();
      const snapNode = new DOMParser()
        .parseFromString(html, 'text/html')
        .querySelector('script[data-cb-cart-snapshot]');
      if (!snapNode) {
        warn('no snapshot script in section response');
        return;
      }
      const snap = JSON.parse(snapNode.textContent || '{}');

      const giftTiers = Array.isArray(snap.giftTiers) ? snap.giftTiers : [];
      log('giftTiers from section', giftTiers);
      if (!giftTiers.length) {
        warn('no giftTiers — gifts_config_handle may be wrong or gifts_config has no gifts');
        return;
      }

      // Count subscription bottles + index existing gift lines.
      //   - `isGift`: line has the `_gift_for` property KEY (works even
      //     when the value is empty/null from older broken adds).
      //   - `giftFor`: the actual handle the line is for. If absent/empty
      //     the line is treated as a stale/orphan gift line — those go
      //     straight onto the remove list so the cart doesn't accumulate
      //     them on repeated reconciles.
      let subscriptionBottles = 0;
      /** @type {Record<string, {key: string}>} */
      const presentGifts = {};
      const orphanGiftKeys = [];
      for (const line of snap.lines || []) {
        if (line.isGift) {
          if (line.giftFor) {
            presentGifts[line.giftFor] = { key: line.key };
          } else if (line.key) {
            orphanGiftKeys.push(line.key);
          }
          continue;
        }
        // Products excluded from gift counting (app → Gifts) don't count.
        if (line.subscription && !line.giftExcluded) {
          subscriptionBottles += Array.isArray(line.bottleSlots) ? line.bottleSlots.length : 0;
        }
      }
      log('counts', {
        subscriptionBottles,
        presentGifts,
        orphanGiftKeys,
        lines: (snap.lines || []).map((l) => ({
          variantId: l.variantId,
          subscription: l.subscription,
          slots: l.bottleSlots?.length,
          isGift: l.isGift,
          giftFor: l.giftFor,
        })),
      });

      // Earned = bottle count meets the tier's required threshold.
      const earned = new Set();
      for (const tier of giftTiers) {
        if (subscriptionBottles >= (tier.required || 0)) earned.add(tier.id);
      }
      log('earned tier ids', [...earned]);

      // Diff.
      const toAdd = [];
      for (const tier of giftTiers) {
        if (!earned.has(tier.id)) continue;
        if (presentGifts[tier.id]) continue;
        if (!tier.variantId) {
          warn('earned tier has no variantId', tier);
          continue;
        }
        // Skip out-of-stock gift variants — adding them would 422. The UI
        // already shows them as OUT OF STOCK so the merchant can restock.
        if (tier.available === false) {
          warn('earned tier is out of stock — skipping add', tier);
          continue;
        }
        toAdd.push({
          id: tier.variantId,
          quantity: 1,
          properties: { _gift_for: tier.id },
        });
      }
      const updates = {};
      for (const [giftId, line] of Object.entries(presentGifts)) {
        if (earned.has(giftId)) continue;
        if (line.key) updates[line.key] = 0;
      }
      // Stale/orphan gift lines (no matching giftFor handle) always go.
      for (const key of orphanGiftKeys) updates[key] = 0;

      log('diff', { toAdd, updates });
      const hasRemovals = Object.keys(updates).length > 0;
      const hasAdditions = toAdd.length > 0;
      if (!hasRemovals && !hasAdditions) return;

      if (hasRemovals) {
        await fetch('/cart/update.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ updates }),
        });
      }
      if (hasAdditions) {
        await fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ items: toAdd }),
        });
      }

      // Re-broadcast so the drawer + bubble + ladder pick up the change.
      const cartRes = await fetch('/cart.js', { cache: 'no-store' });
      const cart = await cartRes.json();
      document.dispatchEvent(
        new CartUpdateEvent({ items: cart.items, source: 'gift-reconciler' }, '', {
          itemCount: cart.item_count,
          source: 'gift-reconciler',
        }),
      );
    } catch (err) {
      warn('failed', err);
    } finally {
      this.#running = false;
    }
  };
}

new GiftReconciler();
